<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  NConfigProvider,
  NDialogProvider,
  NMessageProvider,
  NNotificationProvider,
  darkTheme,
} from 'naive-ui';
import { naiveDateLocale, naiveLocale } from '@/i18n/naive';
import { useNaiveThemeOverrides } from '@/theme/naive-theme';
import { useTheme } from '@/composables/useTheme';
import AppShell from '@/layouts/AppShell.vue';
import ExamLayout from '@/layouts/ExamLayout.vue';
import BareLayout from '@/layouts/BareLayout.vue';
import LoadingState from '@/components/ui/LoadingState.vue';

const route = useRoute();
const { t } = useI18n();
const { isDark } = useTheme();
const themeOverrides = useNaiveThemeOverrides();

/*
 * Birinchi navigatsiya tugamaguncha marshrut YO'Q (`matched` bo'sh): qo'riqchi
 * sessiyani tiklayapti yoki HR'dan kelgan `?p=` bilan kirmoqda — HR so'rovi
 * bir necha soniya olishi mumkin. Bu paytda `meta.layout` bo'sh bo'lgani uchun
 * `AppShell` foydalanuvchisiz chizilib, ilova ochilgandek ko'rinardi (bo'sh
 * menyu, «Tashkilot #0», «Chiqish»). Shuning uchun yuklanish holati.
 */
const booting = computed(() => route.matched.length === 0);

/** Sahifa ochilgandagi manzil — `route.query` bu paytda hali bo'sh */
const fromHr = new URLSearchParams(window.location.search).has('p');

const layout = computed(() => {
  if (booting.value) return BareLayout;

  switch (route.meta.layout) {
    case 'exam':
    case 'reader': // o'qish oynasi ham butun ekran: sidebar/topbar yo'q
      return ExamLayout;
    case 'bare':
      return BareLayout;
    default:
      return AppShell;
  }
});
</script>

<template>
  <!--
    `preflight-style-disabled`: Naive UI o'zining global `body` stilini
    (`font-size: 14px`, `font-family: v-sans`, `line-height: 1.6`) run-time'da
    QATLAMSIZ qilib in'ektsiya qiladi. Bizniki esa `main.css` da `@layer base`
    ichida — CSS kaskadida qatlamsiz qoida har doim qatlamlisidan ustun, ya'ni
    Naive'niki yutardi va butun ilova DS'dagi 16px o'rniga 14px'da chiqardi
    (dizayn tizimi qoidasi: eng kichik shrift 16px). Bizda Tailwind preflight
    va o'z baza stilimiz bor, shuning uchun Naive'niki keraksiz.
  -->
  <NConfigProvider
    preflight-style-disabled
    :theme="isDark ? darkTheme : null"
    :theme-overrides="themeOverrides"
    :locale="naiveLocale"
    :date-locale="naiveDateLocale"
  >
    <NNotificationProvider placement="top-right">
      <NMessageProvider>
        <NDialogProvider>
          <component :is="layout">
            <LoadingState
              v-if="booting"
              :text="fromHr ? t('session.hrEntryPending') : t('common.loading')"
            />
            <RouterView v-else />
          </component>
        </NDialogProvider>
      </NMessageProvider>
    </NNotificationProvider>
  </NConfigProvider>
</template>
